import { useState } from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { format, startOfMonth, endOfMonth, startOfWeek, endOfWeek, addDays, addMonths, isSameMonth, isSameDay, parseISO } from "date-fns";
import { tr } from "date-fns/locale";

export const TenderCalendar = ({ requests, base }) => {
  const [month, setMonth] = useState(startOfMonth(new Date()));
  const gridStart = startOfWeek(month, { weekStartsOn: 1 });
  const gridEnd = endOfWeek(endOfMonth(month), { weekStartsOn: 1 });
  const byDay = {};
  for (const r of requests) {
    if (!r.deadline) continue;
    (byDay[r.deadline] = byDay[r.deadline] || []).push(r);
  }
  const days = [];
  for (let d = gridStart; d <= gridEnd; d = addDays(d, 1)) days.push(d);
  const today = new Date();

  return (
    <div className="border border-slate-200 bg-white" data-testid="tender-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <Button variant="outline" size="sm" onClick={() => setMonth(addMonths(month, -1))} data-testid="calendar-prev" className="rounded-none h-8 border-slate-200 hover:bg-slate-100"><ChevronLeft size={14} /></Button>
        <div className="font-heading font-bold tracking-tight text-lg capitalize" data-testid="calendar-month-label">{format(month, "MMMM yyyy", { locale: tr })}</div>
        <Button variant="outline" size="sm" onClick={() => setMonth(addMonths(month, 1))} data-testid="calendar-next" className="rounded-none h-8 border-slate-200 hover:bg-slate-100"><ChevronRight size={14} /></Button>
      </div>
      <div className="grid grid-cols-7 border-b border-slate-200 text-[10px] uppercase tracking-wider text-slate-400">
        {["Pzt", "Sal", "Çar", "Per", "Cum", "Cmt", "Paz"].map((d) => (
          <div key={d} className="px-2 py-1.5 text-center">{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7">
        {days.map((d) => {
          const key = format(d, "yyyy-MM-dd");
          const events = byDay[key] || [];
          const isToday = isSameDay(d, today);
          return (
            <div key={key} data-testid={`calendar-day-${key}`} className={`min-h-[84px] border-b border-r border-slate-100 p-1.5 ${!isSameMonth(d, month) ? "bg-slate-50/60" : "bg-white"}`}>
              <div className={`text-[11px] mb-1 w-5 h-5 flex items-center justify-center ${isToday ? "bg-slate-900 text-white font-bold" : isSameMonth(d, month) ? "text-slate-600" : "text-slate-300"}`}>
                {format(d, "d")}
              </div>
              <div className="space-y-1">
                {events.map((r) => {
                  const end = parseISO(`${r.deadline}T${r.deadline_time || "23:59"}:00`);
                  const hoursLeft = (end - today) / 3600000;
                  const urgent = hoursLeft > 0 && hoursLeft <= 24;
                  const past = hoursLeft <= 0;
                  return (
                    <Link key={r.id} to={`${base}/${r.id}`} data-testid={`calendar-event-${r.id}`} title={`${r.title} · ${r.deadline_time || "23:59"} · ${r.offer_count || 0} teklif`}
                          className={`block px-1.5 py-1 text-[10px] font-semibold leading-tight truncate transition-colors duration-200 ${past ? "bg-slate-100 text-slate-400 hover:bg-slate-200" : urgent ? "bg-red-500 text-white hover:bg-red-400" : "bg-amber-100 text-amber-800 hover:bg-amber-200 border border-amber-200"}`}>
                      {r.deadline_time || "23:59"} · {r.title}
                    </Link>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>
      <div className="flex items-center gap-4 px-4 py-2.5 border-t border-slate-200 text-[11px] text-slate-500">
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-amber-100 border border-amber-200" /> Aktif son tarih</span>
        <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 bg-red-500" /> 24 saatten az</span>
      </div>
    </div>
  );
};
